// PlannerFamilyAccordion.js - Modified version for Planner with drag-and-drop support
import React, { useState, useRef, useEffect, useCallback } from "react";
import placeholder from "./assets/placeholder.jpg";
import { AiOutlinePlus } from "react-icons/ai";
import { FaSpinner } from "react-icons/fa";
import { getTranslations } from "./components/translations";

const ITEMS_PER_BATCH = 15;
const FETCH_DEBOUNCE_TIME = 500; // ms

function PlannerFamilyAccordion({
  products,
  selectedFamily,
  onProductAdd, // Called on click - adds product to scene
  onProductDrag, // Called on drag - starts drag with ghost
  language = "it",
  emptyImageRef, // For drag ghost image
}) {
  // State initialization
  const [isOpen, setIsOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [contentHeight, setContentHeight] = useState(0);

  // Get translations
  const translations = getTranslations(language);

  // Refs
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);
  const fetchQueueRef = useRef(new Set());
  const fetchTimeoutRef = useRef(null);
  const toggleClickRef = useRef(false);
  const ignoreClickRef = useRef(false);

  // Compute current family products
  const currentFamilyProducts = React.useMemo(() => {
    const family = selectedFamily || "";

    if (family === "") {
      return products;
    }

    return products.filter(
      (product) => product._family === family || product.family === family
    );
  }, [products, selectedFamily]);

  // Reset when family changes
  useEffect(() => {
    if (selectedFamily) {
      setVisibleProducts([]);
      fetchQueueRef.current.clear();
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    }
  }, [selectedFamily]);

  // Update content height
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isOpen, visibleProducts]);

  // Fetch product image
  const fetchProductImage = useCallback(
    async (product) => {
      if (
        !product?.code ||
        loadingStates[product.code] ||
        previewImages[product.code] ||
        failedImages[product.code]
      ) {
        return;
      }

      setLoadingStates((prev) => ({ ...prev, [product.code]: true }));

      try {
        const productUrl = `https://emersya.com/jsShowcase/${product.code}?container=emersyaIframe`;
        const response = await fetch(productUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.text();

        const viewerPreviewImageMatch = data.match(
          /src="(https:\/\/cdn\.emersya\.com\/cl\/[^"]+\/preview\.png\?date=[^"]+)"/
        );

        if (viewerPreviewImageMatch) {
          setPreviewImages((prev) => ({
            ...prev,
            [product.code]: viewerPreviewImageMatch[1],
          }));
        } else {
          setFailedImages((prev) => ({ ...prev, [product.code]: true }));
        }
      } catch (error) {
        console.error(`Failed to fetch preview for ${product.code}:`, error);
        setFailedImages((prev) => ({ ...prev, [product.code]: true }));
      } finally {
        setLoadingStates((prev) => ({ ...prev, [product.code]: false }));
      }
    },
    [loadingStates, previewImages, failedImages]
  );

  // Process fetch queue
  const processFetchQueue = useCallback(() => {
    const queue = Array.from(fetchQueueRef.current);
    fetchQueueRef.current.clear();

    queue.forEach((product) => {
      fetchProductImage(product);
    });
  }, [fetchProductImage]);

  // Queue image fetch
  const queueImageFetch = useCallback(
    (product) => {
      fetchQueueRef.current.add(product);

      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(() => {
        processFetchQueue();
      }, FETCH_DEBOUNCE_TIME);
    },
    [processFetchQueue]
  );

  // Load initial products
  useEffect(() => {
    if (
      isOpen &&
      currentFamilyProducts.length > 0 &&
      visibleProducts.length === 0
    ) {
      const initialBatch = currentFamilyProducts.slice(0, ITEMS_PER_BATCH);
      setVisibleProducts(initialBatch);

      initialBatch.forEach((product) => {
        if (!previewImages[product.code] && !failedImages[product.code]) {
          queueImageFetch(product);
        }
      });
    }
  }, [
    isOpen,
    currentFamilyProducts,
    visibleProducts.length,
    previewImages,
    failedImages,
    queueImageFetch,
  ]);

  // Infinite scroll
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleProducts((prev) => {
          const nextBatch = currentFamilyProducts.slice(
            prev.length,
            prev.length + ITEMS_PER_BATCH
          );

          if (nextBatch.length > 0) {
            nextBatch.forEach((product) => {
              if (!previewImages[product.code] && !failedImages[product.code]) {
                queueImageFetch(product);
              }
            });

            return [...prev, ...nextBatch];
          }

          return prev;
        });
      }
    },
    [currentFamilyProducts, previewImages, failedImages, queueImageFetch]
  );

  // Handle drag start - for dragging products into planner
  const handleDragStart = (e, product) => {
    // ✅ CRITICAL: Prevent defaults FIRST, before anything else
    e.preventDefault();
    e.stopPropagation();

    if (onProductDrag) {
      const plannerProduct = {
        SKU: product.onpagecode || product.name,
        projectCode: product.projectCode,
        code: product.code,
        name: product.name,
        previewPath: previewImages[product.code] || placeholder,
      };

      // ✅ Set drag image BEFORE calling parent
      if (emptyImageRef?.current) {
        e.dataTransfer.setDragImage(emptyImageRef.current, 0, 0);
      }

      // Call parent handler
      onProductDrag(plannerProduct, e);
    }
  };

  // Handle click - alternative to drag
  const handleProductClick = (e, product) => {
    e.stopPropagation();

    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }

    // Call the handler to add product to scene
    if (onProductAdd) {
      onProductAdd(product.code);
    }
  };

  // Handle pointer down
  const handlePointerDown = () => {
    ignoreClickRef.current = false;
  };

  // Toggle accordion
  const toggleAccordion = (e) => {
    e.stopPropagation();
    toggleClickRef.current = true;
    setIsOpen((prev) => !prev);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !toggleClickRef.current &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
      toggleClickRef.current = false;
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get product name in current language
  const getProductName = (product) => {
    if (product.lang && product.lang[language]) {
      return product.lang[language];
    }
    return product.name || product.code;
  };

  return (
    <div className="ay-w-full ay-py-2 ay-border-b ay-border-gray-200">
      <div
        className="ay-group ay-gap-x-4 ay-flex ay-justify-between ay-cursor-pointer"
        onClick={toggleAccordion}
      >
        <div className="ay-flex-grow">
          <div className="ay-flex ay-items-center ay-justify-between">
            <p className="ay-capitalize">{translations.model}: </p>
            <div className="ay-flex ay-items-center ay-justify-center ay-w-6 ay-h-6">
              <span
                className={`ay-transition-transform ay-duration-300 ay-ease-in-out ${
                  isOpen ? "ay-rotate-45" : "ay-rotate-0"
                }`}
              >
                <AiOutlinePlus />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Content */}
      <div
        ref={contentRef}
        className="ay-overflow-hidden ay-transition-all ay-duration-300 ay-ease-in-out"
        style={{
          maxHeight: `${contentHeight}px`,
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div
          className="ay-mt-4 ay-transform ay-transition-transform ay-duration-300 ay-ease-in-out"
          style={{
            transform: isOpen ? "translateY(0)" : "translateY(-10px)",
          }}
        >
          <div ref={dropdownRef} className="ay-w-full">
            {currentFamilyProducts.length > 0 ? (
              <div
                className="ay-max-h-96 ay-overflow-y-auto ay-bg-gray-50 ay-rounded-lg"
                onScroll={handleScroll}
              >
                <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 ay-gap-2 ay-p-2">
                  {visibleProducts.map((product) => {
                    const imageUrl = previewImages[product.code] || placeholder;
                    const isLoading = loadingStates[product.code];
                    const productName = getProductName(product);

                    return (
                      <div
                        key={product.code}
                        className="ay-relative ay-bg-white ay-rounded-lg ay-p-2 ay-cursor-move ay-hover:ay-shadow-md ay-transition-shadow"
                        draggable
                        onDragStart={(e) => handleDragStart(e, product)}
                        onClick={(e) => handleProductClick(e, product)}
                        onPointerDown={handlePointerDown}
                        title={`${productName} - Drag or click to add to planner`}
                      >
                        <div className="ay-relative ay-aspect-square ay-bg-gray-100 ay-rounded ay-overflow-hidden">
                          {isLoading ? (
                            <div className="ay-absolute ay-inset-0 ay-flex ay-items-center ay-justify-center">
                              <FaSpinner className="ay-animate-spin ay-text-gray-400" />
                            </div>
                          ) : (
                            <img
                              src={imageUrl}
                              alt={productName}
                              className="ay-w-full ay-h-full ay-object-cover"
                              loading="lazy"
                            />
                          )}
                        </div>
                        <p className="ay-mt-2 ay-text-xs ay-text-center ay-truncate ay-text-gray-700">
                          {productName}
                        </p>
                        {product.onpage_code && (
                          <p className="ay-text-xs ay-text-center ay-text-gray-400 ay-truncate">
                            {product.onpage_code}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {visibleProducts.length < currentFamilyProducts.length && (
                  <div className="ay-text-center ay-py-2 ay-text-sm ay-text-gray-500">
                    {translations.loadingMore || "Loading more..."}
                  </div>
                )}
              </div>
            ) : (
              <div className="ay-px-4 ay-py-3 ay-text-gray-500 ay-text-center ay-bg-gray-50 ay-rounded-lg">
                {translations.noProductsInFamily ||
                  "No products in this family"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlannerFamilyAccordion;
